import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  BRAND,
  BASE,
  GREY,
  BORDER,
  PRIMARY,
} from "../../utils/constants/colors";
import IconComponent from "../Icon/IconComponent";
import { PersonSearchIcon } from "../Icon/MuiIcons";
import { ICON_LABELS } from "../../utils/constants/UI";
import CustomTooltip from "../Tooltip/CustomTooltip";
import { useTour } from "../../context/TourContext";
import { getTourProgressAction } from "../../redux/actions/guidedTourActions";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../utils/constants";
import { useSetupWizard } from "../../context/SetupWizardContext";

const LOGO_URL =
  "https://images.reachiq.ai/assets/assets/images/new_logo_reachiq.svg";

const KEYFRAMES_CSS = `
  @keyframes wm-welcome-in {
    0%   { opacity: 0; transform: translateY(-44px) scale(0.93); letter-spacing: 0.42em; }
    100% { opacity: 1; transform: translateY(0) scale(1); letter-spacing: 0.18em; }
  }
  @keyframes wm-logo-fly {
    0%   { opacity: 0; transform: translateX(300px) scale(0.55) rotate(-8deg); }
    52%  { opacity: 1; transform: translateX(-22px) scale(1.08) rotate(1.8deg); }
    70%  {             transform: translateX(12px) scale(0.97) rotate(-1deg); }
    84%  {             transform: translateX(-6px) scale(1.01) rotate(0.4deg); }
    100% { opacity: 1; transform: translateX(0) scale(1) rotate(0deg); }
  }
  @keyframes wm-intro-exit {
    0%   { opacity: 1; transform: translate(0, 0) scale(1); }
    100% { opacity: 0; transform: translate(-52px, -38px) scale(0.76); }
  }
  @keyframes wm-glow-pulse {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50%       { opacity: 0.7; transform: scale(1.08); }
  }
  @keyframes wm-logo-header-in {
    0%   { opacity: 0; transform: translateX(28px) scale(1.22); }
    100% { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes wm-content-fade-in {
    0%   { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes wm-card-in {
    0%   { opacity: 0; transform: translateY(14px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .wm-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .wm-card:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important; }
  .wm-btn-primary { transition: background 0.15s ease, transform 0.12s ease !important; }
  .wm-btn-primary:not(:disabled):hover { background: #D23A3A !important; transform: translateY(-1px); }
  .wm-btn-primary:not(:disabled):hover .wm-btn-arrow { transform: translateX(4px); }
  .wm-btn-arrow { transition: transform 0.2s ease; display: inline-flex; align-items: center; line-height: 0; }
  .wm-btn-secondary:not(:disabled):hover { color: #222124 !important; }
`;

const FEATURES = [
  {
    title: "Define Your ICP",
    desc: "Set your Ideal Customer Profile by various criteria. Prospects who do not match your ICP are automatically excluded from your campaigns.",
    tag: "Hyper-Personalization",
    renderIcon: () => (
      <IconComponent iconLabel={ICON_LABELS.TARGET} fontSize="21px" color={BRAND.PRIMARY} />
    ),
  },
  {
    title: "Find Prospects",
    desc: "Search from our massive database of real contacts. Filter by job title, location, and much more to find your ideal prospects for outreach.",
    tag: "Prospect Discovery",
    Icon: PersonSearchIcon,
  },
  {
    title: "Build Marketing Lists",
    desc: "Pick the prospects you want to reach, group them into a list, and assign that list to a campaign as your target audience. Organized outreach, every time.",
    tag: "Audience Management",
    renderIcon: () => (
      <IconComponent iconLabel={ICON_LABELS.LIST_ALT} fontSize="21px" color={BRAND.PRIMARY} />
    ),
  },
  {
    title: "Launch Campaigns",
    desc: "Engage prospects through AI-powered sequences across email, calls, and LinkedIn. Fully automated, personalized, and tracked in real time.",
    tag: "Outreach Automation",
    renderIcon: () => (
      <IconComponent iconLabel={ICON_LABELS.CAMPAIGN} fontSize="21px" color={BRAND.PRIMARY} />
    ),
  },
];

// Small intro size → large content size (px, so CSS transition works)
const INTRO_W = 440;
const INTRO_H = 270;
const CONTENT_W = 820;
const CONTENT_H = 585;

// Mirrors LearningCenter's buildCompletePipeline — all leaf tours from step 0 in order.
const buildCompletePipeline = (tours) => {
  if (!tours || tours.length === 0) return [];
  return [...tours]
    .sort((a, b) => a.order - b.order)
    .flatMap((t) =>
      t.sub_tours && t.sub_tours.length > 0
        ? [...t.sub_tours]
            .sort((a, b) => a.order - b.order)
            .map((s) => ({ key: s.key, fromStep: 0, name: s.name }))
        : [{ key: t.key, fromStep: 0, name: t.name }],
    );
};

export function WelcomeModal({ open, onClose, onExploreSelf }) {
  const [phase, setPhase] = useState(0);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startPipeline } = useTour();
  const { startWizardFromWelcome } = useSetupWizard();

  useEffect(() => {
    if (!open) {
      setPhase(0);
      setLoading(false);
      return;
    }
    const t1 = setTimeout(() => setPhase(1), 180);
    const t2 = setTimeout(() => setPhase(2), 2600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open]);

  const handleStartTour = async () => {
    setLoading(true);
    try {
      // startWizardFromWelcome checks setup status.
      // If all 4 items are already done it fires the tour pipeline immediately.
      // If any are pending it launches the setup wizard; the pipeline fires
      // automatically when the user dismisses the wizard outro.
      await startWizardFromWelcome();
      onClose?.();
    } catch {
      EventEmitter.emit(EMITTER_ERROR, "Something went wrong. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const mW = phase >= 2 ? CONTENT_W : INTRO_W;
  const mH = phase >= 2 ? CONTENT_H : INTRO_H;

  return (
    <>
      <style>{KEYFRAMES_CSS}</style>
      <Modal
        open={open}
        onClose={null}
        disableEscapeKeyDown
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        BackdropProps={{
          style: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.30)",
          },
        }}
      >
        <div
          style={{
            position: "relative",
            width: mW,
            height: mH,
            borderRadius: 18,
            overflow: "hidden",
            outline: "none",
            boxShadow: "0 24px 80px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08)",
            transition: "width 0.65s cubic-bezier(0.22, 1, 0.36, 1), height 0.65s cubic-bezier(0.22, 1, 0.36, 1)",
            background: BASE.WHITE,
          }}
        >
          {/* ══════════════════════════════════════════════════
              PANEL A — INTRO
          ══════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #FFF6F6 0%, #FFFFFF 50%, #F7F6FF 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: phase < 2 ? 2 : 1,
              opacity: phase < 2 ? 1 : 0,
              transition: "opacity 0.55s ease",
              pointerEvents: phase < 2 ? "auto" : "none",
              overflow: "hidden",
            }}
          >
            {/* Glow orb */}
            <div
              style={{
                position: "absolute",
                width: 320,
                height: 180,
                borderRadius: "50%",
                background: "radial-gradient(ellipse at center, rgba(255,72,72,0.09) 0%, rgba(145,98,250,0.07) 45%, transparent 70%)",
                animation: "wm-glow-pulse 3.5s ease-in-out infinite",
                pointerEvents: "none",
              }}
            />

            {/* Inner content — animates toward top-left on exit */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 1,
                animation: phase >= 2 ? "wm-intro-exit 0.55s ease-out forwards" : "none",
              }}
            >
              {phase >= 1 && (
                <div
                  style={{
                    color: GREY.SECONDARY,
                    fontSize: 14,
                    fontWeight: 400,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontFamily: "Inter, sans-serif",
                    animation: "wm-welcome-in 0.75s cubic-bezier(0.22, 1, 0.36, 1) both",
                    marginBottom: 16,
                  }}
                >
                  WELCOME TO
                </div>
              )}
              {phase >= 1 && (
                <img
                  src={LOGO_URL}
                  alt="ReachIQ"
                  style={{
                    height: 52,
                    width: "auto",
                    animation: "wm-logo-fly 1s 0.32s cubic-bezier(0.22, 1, 0.36, 1) both",
                  }}
                />
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════════════════
              PANEL B — CONTENT
          ══════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: BASE.WHITE,
              display: "flex",
              flexDirection: "column",
              zIndex: phase >= 2 ? 2 : 1,
              opacity: phase >= 2 ? 1 : 0,
              animation: phase >= 2
                ? "wm-content-fade-in 0.6s 0.12s cubic-bezier(0.22, 1, 0.36, 1) both"
                : "none",
              overflow: "hidden",
            }}
          >
            {/* Main content */}
            <div
              style={{
                padding: "28px 36px 26px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {/* Header: logo */}
              <div style={{ marginBottom: 18, flexShrink: 0 }}>
                <img
                  src={LOGO_URL}
                  alt="ReachIQ"
                  style={{
                    height: 22,
                    width: "auto",
                    display: "block",
                    animation: phase >= 2
                      ? "wm-logo-header-in 0.6s 0.28s cubic-bezier(0.22, 1, 0.36, 1) both"
                      : "none",
                  }}
                />
              </div>

              {/* Divider */}
              <div
                style={{
                  height: 1,
                  background: BORDER.DEFAULT,
                  marginBottom: 16,
                  flexShrink: 0,
                }}
              />

              {/* Section label */}
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: GREY.TERTIARY,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  marginBottom: 14,
                  fontFamily: "Inter, sans-serif",
                  flexShrink: 0,
                }}
              >
                WHAT YOU CAN DO WITH REACHIQ
              </div>

              {/* 2×2 Cards grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  overflow: "visible",
                }}
              >
                {FEATURES.map((feat, i) => (
                  <div
                    key={feat.title}
                    className="wm-card"
                    style={{
                      background: BASE.WHITE,
                      borderRadius: 10,
                      padding: "13px 16px 13px",
                      border: `1px solid ${BORDER.DEFAULT}`,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                      display: "flex",
                      flexDirection: "column",
                      animation: phase >= 2
                        ? `wm-card-in 0.5s ${0.22 + i * 0.07}s cubic-bezier(0.22, 1, 0.36, 1) both`
                        : "none",
                    }}
                  >
                    {/* Icon badge */}
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        background: PRIMARY.W_50,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 8,
                        flexShrink: 0,
                      }}
                    >
                      {feat.renderIcon
                        ? feat.renderIcon()
                        : <feat.Icon style={{ fontSize: 21, color: BRAND.PRIMARY }} />}
                    </div>

                    <h4
                      style={{
                        margin: "0 0 5px",
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: GREY.PRIMARY,
                        fontFamily: "Inter, sans-serif",
                        lineHeight: 1.3,
                        flexShrink: 0,
                      }}
                    >
                      {feat.title}
                    </h4>

                    <p
                      style={{
                        margin: "0 0 0",
                        fontSize: 13.5,
                        color: GREY.SECONDARY,
                        lineHeight: 1.65,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {feat.desc}
                    </p>

                    <span
                      style={{
                        display: "inline-block",
                        background: BASE.BACKGROUND_LIGHT,
                        color: GREY.SECONDARY,
                        border: `1px solid ${BORDER.DARK}`,
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "3px 9px",
                        borderRadius: 20,
                        fontFamily: "Inter, sans-serif",
                        alignSelf: "flex-start",
                        marginTop: 16,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {feat.tag}
                    </span>
                  </div>
                ))}
              </div>

              {/* Divider above buttons */}
              <div
                style={{
                  height: 1,
                  background: BORDER.DEFAULT,
                  marginTop: 16,
                  flexShrink: 0,
                  animation: phase >= 2 ? "wm-card-in 0.45s 0.48s cubic-bezier(0.22, 1, 0.36, 1) both" : "none",
                }}
              />

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 10,
                  paddingTop: 14,
                  flexShrink: 0,
                  animation: phase >= 2 ? "wm-card-in 0.45s 0.50s cubic-bezier(0.22, 1, 0.36, 1) both" : "none",
                }}
              >
                <CustomTooltip
                  title="You can take a tour anytime from Settings > Learning Center."
                  placement="top"
                  theme="dark"
                  slotProps={{ tooltip: { sx: { maxWidth: 260, maxHeight: "unset" } } }}
                >
                  <button
                    className="wm-btn-secondary"
                    onClick={onExploreSelf ?? onClose}
                    disabled={loading}
                    style={{
                      background: "none",
                      border: "none",
                      color: GREY.TERTIARY,
                      fontSize: 13,
                      cursor: loading ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      padding: "8px 12px",
                      transition: "color 0.15s ease",
                      opacity: loading ? 0.45 : 1,
                    }}
                  >
                    I'll Explore Myself
                  </button>
                </CustomTooltip>

                <button
                  className="wm-btn-primary"
                  onClick={handleStartTour}
                  disabled={loading}
                  style={{
                    background: BRAND.PRIMARY,
                    color: BASE.WHITE,
                    border: "none",
                    borderRadius: 8,
                    padding: "0 22px",
                    height: 38,
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    lineHeight: 1,
                    opacity: 1,
                    minWidth: 120,
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={15} thickness={4.5} style={{ color: BASE.WHITE }} />
                  ) : (
                    <>
                      Take a Tour
                      <span className="wm-btn-arrow">
                        <IconComponent
                          iconLabel={ICON_LABELS.NAVIGATE_NEXT}
                          fontSize={18}
                          color={BASE.WHITE}
                        />
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
